import { ExportProgressReporter } from "../components/CustomButton";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import {
  ADMIN_DATA_SUBTITLES,
  adminNavItemSx,
  adminNavPanelSx,
  adminPageSx,
  adminNavTitleSx,
} from "../components/adminData/adminNavStyles";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import { getAllBusinesses } from "../services/VenueService";
import { listExpenses } from "../services/newExpensesServices";
import PinAccessDialog from "../components/PinAccessDialog";
import {
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";

type AdminTab = "clients" | "finance" | "expenses" | "reports" | "notifications";

interface AdminExpenseRow {
  id: string;
  title?: string;
  description?: string;
  amount?: string | number;
  date?: string;
  business_name?: string;
  business_id?: string | number;
  unit?: string;
  note?: string;
  createdBy?: string;
  unit_id?: string | number;
  [key: string]: any;
}

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, day, month, year, hour, minute, second] = match;
  const d = Number(day);
  const m = Number(month) - 1;
  const y = Number(year);
  const h = Number(hour || "0");
  const min = Number(minute || "0");
  const sec = Number(second || "0");
  const date = new Date(y, m, d, h, min, sec);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeExpenseRow = (item: any, index: number, businessesMap?: Map<string, string>): AdminExpenseRow => {
  const businessIdCandidate =
    item?.business_id ??
    item?.businessId ??
    item?.business?.id ??
    item?.business?.business_id ??
    item?.metadata?.businessId;
  const businessName =
    (businessIdCandidate && businessesMap?.get(String(businessIdCandidate))) ||
    pickNonEmptyString(
      item?.business_name,
      item?.businessName,
      item?.business?.name,
      item?.business
    ) || "";
  const unitName =
    pickNonEmptyString(
      item?.unit_name,
      item?.unitName,
      item?.unit?.name,
      item?.unit_name,
      item?.unit?.name,
      item?.unit,
      item?.unit
    ) || "Unknown Unit";
  const dateValue =
    pickNonEmptyString(
      item?.date,
      item?.expense_date,
      item?.created_at,
      item?.createdAt
    ) || "";

  return {
    id: String(
      item?.id ||
        item?._id ||
        item?.expense_id ||
        item?.expenseId ||
        `expense-${index}`
    ),
    title: pickNonEmptyString(item?.title, item?.name) || "",
    description: item?.description || "",
    amount: item?.amount ?? item?.total ?? 0,
    date: dateValue,
    business_name: businessName,
    business_id:
      businessIdCandidate !== undefined && businessIdCandidate !== null
        ? String(businessIdCandidate)
        : undefined,
    unit: unitName,
    note: item?.notes || item?.note || "",
    createdBy: item?.createdBy || item?.user_name || item?.user_id || "",
    unit_id:
      item?.unit_id || item?.unitId,
  };
};

const ADMIN_EXPENSES_PAGE_SIZE = 30;

const AdminExpenses: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "expenses";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [expenses, setExpenses] = useState<AdminExpenseRow[]>([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [financeUnlocked, setFinanceUnlocked] = useState(() =>
    getCachedPinAccess()
  );
  const [pinDialogOpen, setPinDialogOpen] = useState(() =>
    !getCachedPinAccess()
  );
  const [expenseDateRange, setExpenseDateRange] = useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>([]);
const [businessFilter, setBusinessFilter] = useState<string>("all");

  const navigateTo = useCallback(
    (path: string) => {
      if (!financeUnlocked) {
        setPinDialogOpen(true);
        return;
      }
      navigate(path);
    },
    [financeUnlocked, navigate]
  );

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await getAllBusinesses();
        const mapped = (Array.isArray(response) ? response : [])
          .map((business: any) => {
            const id = business?.id;
            if (!id) return null;
            const name =
              pickNonEmptyString(
                business?.name,
                business?.business_name,
                business?.title
              ) || `Business ${id}`;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];
        setBusinesses(mapped);
      } catch (error) {
        console.error("Error fetching businesses:", error);
        setBusinesses([]);
      }
    };
    fetchBusinesses();
  }, []);
  useEffect(() => {
    const unsubscribe = subscribeToPinAccess((value) => {
      setFinanceUnlocked(value);
      if (!value) setPinDialogOpen(true);
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (financeUnlocked) {
      setPinDialogOpen(false);
    }
  }, [financeUnlocked]);

  const handlePinVerified = () => {
    setFinanceUnlocked(true);
    setPinDialogOpen(false);
  };
// One page of expenses; business, dates and search are filtered by the
// server, which also sums the amounts over every match.
const loadExpensesPage = useCallback(
  async ({
    page,
    size,
    search: searchTerm,
    withSummary,
  }: {
    page: number;
    size: number;
    search?: string;
    withSummary?: boolean;
  }) => {
    const response = await listExpenses({
      businessId: businessFilter !== "all" ? businessFilter : undefined,
      from:
        expenseDateRange && !Array.isArray(expenseDateRange)
          ? buildDateBoundary(expenseDateRange.start, false)?.toISOString()
          : undefined,
      to:
        expenseDateRange && !Array.isArray(expenseDateRange)
          ? buildDateBoundary(expenseDateRange.end, true)?.toISOString()
          : undefined,
      page,
      size,
      search: String(searchTerm || "").trim() || undefined,
      summary: withSummary,
    }) as any;

    const api = response?.data || response;
    const expensesData =
      api?.expenses ||
      api?.content ||
      api?.data?.expenses ||
      (Array.isArray(api) ? api : []);

    const businessesMap = new Map(businesses.map((v) => [v.id, v.name]));
    const rows: AdminExpenseRow[] = expensesData.map((item: any, index: number) =>
      normalizeExpenseRow(item, page * size + index, businessesMap)
    );
    return {
      rows,
      totalElements: Number(api?.totalElements) || rows.length,
      totalPages: Math.max(1, Number(api?.totalPages) || 1),
      summary: api?.summary ?? null,
    };
  },
  [businessFilter, expenseDateRange, businesses]
);


  const [expensesSummary, setExpensesSummary] = useState<any | null>(null);
  const debouncedSearch = useDebouncedValue(search, 400);
  // Summed by the server over every expense matching the filters.
  const totalAmount = Number(expensesSummary?.totalAmount) || 0;

  const fetchExpensesTable = useCallback(
    async (params?: any): Promise<IPageable<AdminExpenseRow>> => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || ADMIN_EXPENSES_PAGE_SIZE;
      if (!financeUnlocked) {
        return { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      }
      setLoadingExpenses(true);
      try {
        const result = await loadExpensesPage({
          page,
          size,
          search: String(params?.search || ""),
          withSummary: true,
        });
        setExpenses(result.rows);
        setExpensesSummary(result.summary);
        setTotalElements(result.totalElements);
        return {
          content: result.rows,
          number: page,
          size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        };
      } catch (error) {
        console.error("Error fetching expenses:", error);
        setExpenses([]);
        setExpensesSummary(null);
        throw error;
      } finally {
        setLoadingExpenses(false);
      }
    },
    [financeUnlocked, loadExpensesPage]
  );

  const exportExpensesCsv = useCallback(async (reportProgress?: ExportProgressReporter) => {
    // The table holds one page; the file gets every matching expense.
    const filteredExpenses: AdminExpenseRow[] = [];
    try {
      for (let page = 0; page < 1000; page += 1) {
        const result = await loadExpensesPage({ page, size: 200, search: debouncedSearch });
        filteredExpenses.push(...result.rows);
        reportProgress?.(page + 1, result.totalPages);
        if (page + 1 >= result.totalPages || result.rows.length === 0) break;
      }
    } catch (error) {
      console.error("Error exporting expenses:", error);
      return;
    }
    if (filteredExpenses.length === 0) return;
    const keys = ["description", "amount", "date", "business_name"];
    const header = keys.join(",");
    const lines = filteredExpenses.map((r: any) => {
      const row: Record<string, any> = {
        description: r.description ?? "-",
        amount: r.amount ?? "-",
        date: r.date ?? "-",
        business_name: r.business_name ?? "-",
      };
      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [debouncedSearch, loadExpensesPage]);


  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    { key: "finance", label: "Invoices", icon: <AccountBalanceWalletOutlinedIcon /> },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
    {
      key: "notifications",
      label: "Notification Devices",
      icon: <NotificationsActiveOutlinedIcon />,
    },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";

  return (
    <Box
      sx={adminPageSx}
    >
      <Box
        sx={adminNavPanelSx(sidebarCollapsed)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" sx={adminNavTitleSx} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "finance") {
                      navigateTo("/all-data/invoices");
                    } else if (item.key === "reports") {
                      navigateTo("/all-data/reports");
                    } else if (item.key === "notifications") {
                      navigate("/all-data/notification-devices");
                    }
                  }}
                  sx={adminNavItemSx(selected, sidebarCollapsed)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          // Page is pulled up under the header (adminPageSx); give it back here.
          pt: { xs: 3, sm: 5 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={activeTabLabel}
          subtitle={ADMIN_DATA_SUBTITLES[activeTab]}
          icon={navItems.find((item) => item.key === activeTab)?.icon}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...businesses.map((business) => ({
                  label: business.name,
                  value: business.id,
                })),
              ],
              value: businessFilter,
              onChange: (v: any) => {
                const next =
                  v === null || v === undefined || v === "" ? "all" : String(v);
                setBusinessFilter(next);
              },
              placeholder: "Business",
              label: "Business",
              size: "small",
              searchable: false,
              clearable: true,
              sx: { width: 200 },
            }}
            dateRangeConfig={{
              value: expenseDateRange ?? undefined,
              onChange: (v: any) => setExpenseDateRange(v),
              fullWidth: false,
            }}
            buttonConfig={[
              {
                label: "Export CSV",
                onClick: (reportProgress) => exportExpensesCsv(reportProgress),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                disabled: !financeUnlocked,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminExpenseRow>
              filters={{
                page: 0,
                size: ADMIN_EXPENSES_PAGE_SIZE,
                search: debouncedSearch,
                businessFilter,
                dateRange: JSON.stringify(expenseDateRange ?? null),
                financeUnlocked,
              }}
              fields={[
                { key: "description", header: "Description" },
                { key: "amount", header: "Amount" },
                { key: "date", header: "Date" },
                { key: "business_name", header: "Business" },
              ]}
              fetchFunc={fetchExpensesTable}
              loading={loadingExpenses}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: COLORS.WhiteBackground,
              borderRadius: "0 0 10px 10px",
              padding: "16px",
              marginTop: "-10px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomLabel
              text="Total Amount:"
              sx={{ color: COLORS.typographyColor }}
            />
            <CustomLabel
              text={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalAmount)}
              fontWeight="bold"
              sx={{ color: COLORS.MainColor }}
            />
          </Box>
        </Box>
      </Box>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        onVerified={handlePinVerified}
        title="Finance access"
        description="Enter the PIN to view admin expenses."
      />
    </Box>
  );
};

export default AdminExpenses;
